import { IfData } from 'src/utils/insuranceFund';
import { SpotMarketConfig } from '@velocity-exchange/sdk';
import { findDaysHoursMinutesFromNow, printDate } from 'src/utils/dateTime';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { useMemo } from 'react';
import { MarketId } from '@velocity-exchange/common';
import useUtilizationIsTooHighForIFUnstake from 'src/hooks/useUtilizationIsTooHighForIFUnstake';
import UnstakeUtilizationCutoffMessage from 'src/components/Earn/InsuranceFundUnstakeUtilizationCutoffMessage';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';

interface WarningProps {
	ifData: IfData | undefined;
	spotMarket: SpotMarketConfig;
}

export const CardWarning = ({ ifData, spotMarket }: WarningProps) => {
	const selectedMarketId: MarketId = useMemo(
		() => MarketId.createSpotMarket(spotMarket.marketIndex),
		[spotMarket.marketIndex]
	);

	const unstakeUtilizationCutoffResult =
		useUtilizationIsTooHighForIFUnstake(selectedMarketId);

	const displayDecimals =
		useMarketsInfoStore(
			(s) =>
				s.allMarketsInfo?.spotLookup?.[spotMarket.marketIndex]?.genericInfo
					?.baseDisplayDecimals
		) ?? 2;

	if (!ifData) return null;

	const unstakeRequestTimestamp = ifData.userStake?.unstakeRequest?.timestamp; // in ms
	const unstakePeriod =
		(ifData.vault.account?.unstakingPeriod.toNumber() ?? 0) * 1000; // in ms

	const unstakeInProgress = !!unstakeRequestTimestamp;

	const unstakeTimeoutExpired =
		unstakeInProgress &&
		Date.now() >= (unstakeRequestTimestamp ?? 0) + unstakePeriod;

	const canWithdraw =
		unstakeInProgress &&
		!unstakeUtilizationCutoffResult.unstakingIsDisabled &&
		unstakeTimeoutExpired;

	const unstakeDate = new Date((unstakeRequestTimestamp ?? 0) + unstakePeriod);
	const unstakeFullDateDisplay = printDate(new Date(unstakeDate));
	const unstakeShortDateDisplay = findDaysHoursMinutesFromNow(unstakeDate);

	const showOverUtilizationWarning =
		unstakeInProgress && unstakeUtilizationCutoffResult.unstakingIsDisabled;

	const showAmountAvailableToWithdraw = unstakeInProgress && canWithdraw;

	const showWithdrawTimeRemaining = unstakeInProgress && !unstakeTimeoutExpired;

	const unstakeRequestAmount = ifData.userStake?.unstakeRequest?.amount ?? 0;

	if (!unstakeInProgress) {
		return null;
	}

	return (
		<div className="flex w-full flex-col gap-y-md">
			{showOverUtilizationWarning && (
				<div className="flex w-full flex-col">
					<UnstakeUtilizationCutoffMessage
						{...unstakeUtilizationCutoffResult}
						type="IFPage"
					/>
				</div>
			)}
			{showAmountAvailableToWithdraw && (
				<InlineAlert
					variant="info"
					title={`Available to withdraw: ${unstakeRequestAmount.toFixed(
						displayDecimals
					)} ${spotMarket.symbol}`}
				/>
			)}
			{showWithdrawTimeRemaining && (
				<InlineAlert
					variant="info"
					title={
						<>
							{unstakeRequestAmount.toFixed(displayDecimals)}{' '}
							{spotMarket.symbol} unstaking{' '}
							<Tooltip
								content={unstakeFullDateDisplay}
								disabled={unstakeShortDateDisplay === 'now'}
							>
								{unstakeShortDateDisplay === 'now'
									? 'now'
									: `in ${unstakeShortDateDisplay}`}
							</Tooltip>
						</>
					}
				/>
			)}
		</div>
	);
};
